"use client";

import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { fetchClientStats } from "@/store/slices/landingSlice";

/* Fallback stats if API returns empty */
const FALLBACK_STATS = [
  { _id: "1", value: "15+", label: "Global Industry Awards" },
  { _id: "2", value: "99.8%", label: "Client Satisfaction" },
  { _id: "3", value: "50M+", label: "Active Users Reached" },
  { _id: "4", value: "250+", label: "Completed Projects" },
];

/* Animated counter that runs up to the numeric part of a value string */
function useCountUp(target: string, active: boolean) {
  const numericMatch = target ? target.match(/[\d,.]+/) : null;
  const numeric = numericMatch ? parseFloat(numericMatch[0].replace(/,/g, "")) : 0;
  const suffix = target ? target.replace(/[\d,.]+/, "") : "";
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    if (!active || numeric === 0) return;
    setStarted(true);
    const duration = 1800;
    const steps = 60;
    const increment = numeric / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= numeric) {
        setCount(numeric);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, duration / steps);
    return () => clearInterval(timer);
  }, [active, numeric]);

  return { display: started ? count.toLocaleString() + suffix : target || "0" };
}

const StatCard = ({
  value,
  label,
  index,
  isDark,
}: {
  value: string;
  label: string;
  index: number;
  isDark: boolean;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });
  const { display } = useCountUp(value, isInView);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1, duration: 0.5, ease: "easeOut" }}
      className="flex flex-col items-center text-center px-4 sm:px-6 py-6 sm:py-7 relative group"
    >
      {/* Balanced elegant number */}
      <span
        className="text-3xl sm:text-4xl font-bold tracking-tight bg-gradient-to-br from-[#8B31FF] to-[#6D28D9] bg-clip-text text-transparent leading-tight mb-2 transition-transform duration-300 group-hover:scale-105"
      >
        {display}
      </span>

      <span
        className={`text-xs sm:text-sm font-semibold uppercase tracking-wider ${
          isDark ? "text-gray-400" : "text-gray-600"
        }`}
      >
        {label}
      </span>

      {/* Vertical divider — hidden on last item */}
      <div
        className={`hidden lg:block absolute right-0 top-1/2 -translate-y-1/2 h-10 w-px ${
          isDark
            ? "bg-gradient-to-b from-transparent via-white/10 to-transparent"
            : "bg-gradient-to-b from-transparent via-gray-200 to-transparent"
        } last:hidden`}
      />
    </motion.div>
  );
};

const AboutStatsSection = () => {
  const { mode } = useAppSelector((state) => state.theme);
  const dispatch = useAppDispatch();
  const { data: stats } = useAppSelector((state) => state.landing.clientStats);
  const isDark = mode === "dark";

  useEffect(() => {
    dispatch(fetchClientStats());
  }, [dispatch]);

  const displayStats = stats && stats.length > 0 ? stats.slice(0, 4) : FALLBACK_STATS;

  return (
    <section
      className={`relative py-16 sm:py-20 overflow-hidden transition-colors duration-700 ${
        isDark ? "bg-[#0B0F19]" : "bg-gradient-to-b from-white via-slate-50 to-white"
      }`}
    >
      {/* Background (matches AboutHeroSection) */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className={`absolute inset-0 transition-opacity duration-700 ${isDark ? "opacity-[0.05]" : "opacity-[0.04]"}`}
          style={{
            backgroundImage: isDark
              ? `linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)`
              : `linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)`,
            backgroundSize: "40px 40px",
          }}
        />
        <motion.div
          className={`absolute -left-24 top-8 h-[420px] w-[420px] rounded-full blur-[110px] ${isDark ? "bg-[#8B31FF]/25" : "bg-[#8B31FF]/12"}`}
          animate={{ x: [0, 70, 0], y: [0, 25, 0] }}
          transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className={`absolute -right-20 bottom-0 h-[460px] w-[460px] rounded-full blur-[120px] ${isDark ? "bg-[#8B31FF]/18" : "bg-[#8B31FF]/10"}`}
          animate={{ x: [0, -80, 0], y: [0, 30, 0] }}
          transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Label */}
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center text-xs font-bold uppercase tracking-[0.25em] mb-8 text-[#8B31FF]"
        >
          By The Numbers
        </motion.p>

        {/* Stats strip */}
        <div
          className={`rounded-lg sm:rounded-3xl border backdrop-blur-sm shadow-sm ${
            isDark
              ? "bg-white/[0.03] border-white/8 shadow-black/20"
              : "bg-white/90 border-gray-100 shadow-purple-500/5"
          }`}
        >
          <div
            className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 lg:divide-x ${
              isDark ? "divide-white/8" : "divide-gray-100"
            }`}
          >
            {displayStats.map((stat, index) => (
              <StatCard
                key={(stat as any)._id || (stat as any).id || index}
                value={stat.value}
                label={stat.label}
                index={index}
                isDark={isDark}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Bottom border glow */}
      <div
        className={`absolute bottom-0 left-1/2 -translate-x-1/2 w-[700px] h-px ${
          isDark
            ? "bg-gradient-to-r from-transparent via-[#8B31FF]/30 to-transparent"
            : "bg-gradient-to-r from-transparent via-[#8B31FF]/20 to-transparent"
        }`}
      />
    </section>
  );
};

export default AboutStatsSection;
